/**
 * Tiny vendored polyfills for the handful of APIs our dependencies call on
 * browsers below where those APIs shipped but above our real floor
 * (Chrome/Edge 84, Firefox 78, Safari 14.1).
 *
 * MUST be the first import in main.tsx, so these exist before livekit-client or
 * the lazy Excalidraw tree evaluates. Everything here is a few lines, standard
 * behaviour, installed only when missing — no library, no CDN (the CSP allows
 * no third-party origin anyway).
 *
 * What each one protects, verified by scanning the BUILT chunks:
 * - crypto.randomUUID   → livekit data-channel/RPC ids (UNGUARDED there);
 *                         Chrome 92 / Safari 15.4. Without it, in-room features
 *                         throw on Chrome 84–91 and iPads on Safari 14–15.3.
 * - Object.hasOwn       → Excalidraw tree; Chrome 93 / Safari 15.4.
 * - Array.prototype.at  → Excalidraw tree; Chrome 92 / Safari 15.4.
 * - String.replaceAll   → Excalidraw tree; Chrome 85 / Safari 13.1.
 *
 * structuredClone is deliberately NOT polyfilled: livekit guards its own use,
 * and the only unguarded caller is Excalidraw's mermaid text-to-diagram path. A
 * home-grown clone that silently mishandles an exotic type corrupts state;
 * a loud "function missing" on a 5-year-old browser inside an optional board
 * sub-feature is the better failure. (Codex review concurred.)
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

if (typeof crypto !== 'undefined' && typeof crypto.randomUUID !== 'function') {
  // RFC 4122 v4 from getRandomValues, which IS available at our floor.
  ;(crypto as any).randomUUID = function randomUUID(): string {
    const bytes = crypto.getRandomValues(new Uint8Array(16))
    bytes[6] = (bytes[6] & 0x0f) | 0x40
    bytes[8] = (bytes[8] & 0x3f) | 0x80
    const hex: string[] = []
    for (let i = 0; i < 16; i += 1) hex.push(bytes[i].toString(16).padStart(2, '0'))
    return (
      hex.slice(0, 4).join('') +
      '-' +
      hex.slice(4, 6).join('') +
      '-' +
      hex.slice(6, 8).join('') +
      '-' +
      hex.slice(8, 10).join('') +
      '-' +
      hex.slice(10, 16).join('')
    )
  }
}

if (typeof (Object as any).hasOwn !== 'function') {
  ;(Object as any).hasOwn = (obj: object, key: PropertyKey) => Object.hasOwn(obj, key)
}

if (typeof (Array.prototype as any).at !== 'function') {
  // eslint-disable-next-line no-extend-native
  Object.defineProperty(Array.prototype, 'at', {
    value: function at(this: unknown[], n: number) {
      const i = Math.trunc(n) || 0
      const index = i < 0 ? this.length + i : i
      return index >= 0 && index < this.length ? this[index] : undefined
    },
    writable: true,
    configurable: true,
  })
}

if (typeof (String.prototype as any).replaceAll !== 'function') {
  // eslint-disable-next-line no-extend-native
  Object.defineProperty(String.prototype, 'replaceAll', {
    value: function replaceAll(this: string, search: string | RegExp, replacement: any) {
      if (search instanceof RegExp) {
        // Spec: a non-global regex is a TypeError, not a silent single replace.
        if (!search.flags.includes('g')) {
          throw new TypeError('replaceAll must be called with a global RegExp')
        }
        return this.replace(search, replacement)
      }
      // Always the escaped-regex path: split/join looked simpler but diverges
      // from spec on an empty search ('abc' → '+a+b+c+', not 'a+b+c') and does
      // not process $-substitution patterns in the replacement string.
      const escaped = String(search).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      return this.replace(new RegExp(escaped, 'g'), replacement)
    },
    writable: true,
    configurable: true,
  })
}

export {}
